import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect';

export type UseCollapsedRowsOptions = {
  containerRef: RefObject<HTMLElement | null>;
  itemCount: number;
  /** Changes whenever item widths may change with the same count (titles, counters, language). */
  contentKey?: string;
  maxRows: number;
  enabled: boolean;
  itemSelector: string;
  toggleSelector: string;
};

export type UseCollapsedRowsResult = {
  /** `null` until measured — the consumer must render every item visible in that state. */
  visibleCount: number | null;
  hasOverflow: boolean;
};

type Measurement = {
  result: UseCollapsedRowsResult;
  itemCount: number;
  contentKey: string | undefined;
  maxRows: number;
};

export const NOT_MEASURED: UseCollapsedRowsResult = { visibleCount: null, hasOverflow: false };

/** Sub-pixel baseline differences must not split one flex row into two. */
const ROW_TOP_TOLERANCE = 1;

const groupIntoRows = (items: HTMLElement[]): HTMLElement[][] => {
  const rows: HTMLElement[][] = [];
  let rowTop = Number.NEGATIVE_INFINITY;

  items.forEach((item) => {
    const { top } = item.getBoundingClientRect();

    if (top > rowTop + ROW_TOP_TOLERANCE) {
      rows.push([]);
      rowTop = top;
    }

    rows[rows.length - 1].push(item);
  });

  return rows;
};

const measure = (
  container: HTMLElement,
  {
    itemSelector,
    toggleSelector,
    maxRows,
  }: Pick<UseCollapsedRowsOptions, 'itemSelector' | 'toggleSelector' | 'maxRows'>
): UseCollapsedRowsResult => {
  const items = Array.from(container.querySelectorAll<HTMLElement>(itemSelector));
  const rows = groupIntoRows(items);

  if (rows.length <= maxRows) {
    return { visibleCount: items.length, hasOverflow: false };
  }

  const lastRow = rows[maxRows - 1];
  const firstIndexOfLastRow = rows
    .slice(0, maxRows - 1)
    .reduce((count, row) => count + row.length, 0);

  const toggleWidth =
    container.querySelector<HTMLElement>(toggleSelector)?.getBoundingClientRect().width ?? 0;
  const containerStyle = getComputedStyle(container);
  const gap = parseFloat(containerStyle.columnGap) || 0;
  // LTR only: free space is measured from an item's right edge to the content-box right edge.
  const limit =
    container.getBoundingClientRect().right - (parseFloat(containerStyle.paddingRight) || 0);

  let fit = lastRow.length;

  while (fit > 0 && lastRow[fit - 1].getBoundingClientRect().right + gap + toggleWidth > limit) {
    fit -= 1;
  }

  return { visibleCount: firstIndexOfLastRow + fit, hasOverflow: true };
};

/**
 * Clamps a wrapping LTR flex list to `maxRows`; measured in a layout effect, so it lands before paint.
 * Re-measures on container width change, `itemCount`/`contentKey`/`maxRows` change and a late font swap.
 */
export const useCollapsedRows = ({
  containerRef,
  itemCount,
  contentKey,
  maxRows,
  enabled,
  itemSelector,
  toggleSelector,
}: UseCollapsedRowsOptions): UseCollapsedRowsResult => {
  const [measured, setMeasured] = useState<Measurement | null>(null);
  const measuredWidthRef = useRef<number | null>(null);

  const isStale =
    measured === null ||
    measured.itemCount !== itemCount ||
    measured.contentKey !== contentKey ||
    measured.maxRows !== maxRows;

  useIsomorphicLayoutEffect(() => {
    if (!enabled) {
      if (measured !== null) {
        setMeasured(null);
      }

      return;
    }

    const container = containerRef.current;

    if (!isStale || !container) {
      return;
    }

    measuredWidthRef.current = container.getBoundingClientRect().width;
    setMeasured({
      result: measure(container, { itemSelector, toggleSelector, maxRows }),
      itemCount,
      contentKey,
      maxRows,
    });
  }, [
    enabled,
    isStale,
    measured,
    itemCount,
    contentKey,
    maxRows,
    itemSelector,
    toggleSelector,
    containerRef,
  ]);

  useEffect(() => {
    const container = containerRef.current;

    if (!enabled || !container || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      const { width } = container.getBoundingClientRect();

      // Only a width change can re-wrap the rows; our own hide/show changes the height.
      if (measuredWidthRef.current !== null && Math.abs(width - measuredWidthRef.current) > 0.5) {
        setMeasured(null);
      }
    });

    observer.observe(container);

    return () => observer.disconnect();
    // Same inputs as a fresh measurement: a consumer that rendered null (empty list) gets its
    // container observed once the items arrive.
  }, [enabled, containerRef, itemCount, contentKey, maxRows]);

  useEffect(() => {
    // `fonts.ready` resolves immediately once everything is loaded — nothing to wait for then.
    if (
      !enabled ||
      typeof document === 'undefined' ||
      !document.fonts ||
      document.fonts.status === 'loaded'
    ) {
      return undefined;
    }

    let cancelled = false;

    // A late web-font swap changes item widths without resizing the container.
    document.fonts.ready.then(() => {
      if (!cancelled) {
        setMeasured(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return enabled && !isStale ? measured.result : NOT_MEASURED;
};
